import {
  buildSpeakerColorMap,
  getSpeakerColor,
  isDisplayableDiarization,
  SpeakerColorEntry,
} from '@app/modules/diarization/speaker-utils';
import { DiarizationType } from '@app/modules/search/types';
import { usePersonsByIds } from '@app/modules/persons/hooks/usePersonsByIds';
import { DateTimeStr, TimeRange, toDateTimeStr, toTimestamp } from '@mi-front/core';
import { useMemo } from 'react';
import { Station } from '@app/core/types/stations';
import { useLoggerStreamTranscriptions } from './useLoggerStreamTranscriptions';

/**
 * Fetches hour-bucketed transcriptions with diarization for a station range,
 * derives speaker colors, and batch-warms Person API cache for any person_ids.
 */
export function useDiarization(
  station: Station,
  range: TimeRange<DateTimeStr>,
  diarizationEnabled = true,
) {
  const { data, refetch, isFetched } = useLoggerStreamTranscriptions(
    station,
    range,
    diarizationEnabled,
    { enabled: diarizationEnabled },
  );

  const flatEntries = useMemo(() => {
    if (!isFetched || !diarizationEnabled || !data) {
      return [];
    }
    // PNT DTO omits optional `voice_label`; cast to shared UI diarization shape.
    return data
      .flat()
      .flatMap((transcription) => (transcription?.diarization ?? []) as DiarizationType[]);
  }, [isFetched, data, diarizationEnabled]);

  const personIds = useMemo(() => {
    return flatEntries
      .map((d) => d.person_id)
      .filter((id): id is string => typeof id === 'string' && id.length > 0);
  }, [flatEntries]);

  // Batch-fetch + warm individual person query cache for the whole stream range.
  usePersonsByIds(personIds);

  const speakers: SpeakerColorEntry[] = useMemo(
    () => buildSpeakerColorMap(flatEntries),
    [flatEntries],
  );

  const formatedData = useMemo(() => {
    if (!isFetched || !diarizationEnabled || !data) {
      return [];
    }

    const mappedData = data.flat().map((transcription) => {
      const diarization = (transcription?.diarization ?? []) as DiarizationType[];
      if (!diarization.length) {
        return null;
      }
      return diarization.map((d) => {
        if (!isDisplayableDiarization(d)) {
          return null;
        }
        return {
          time: toDateTimeStr(toTimestamp(transcription.time) + d.segment.start),
          distance: d.distance,
          label: d.label,
          segment: d.segment,
          voice_label: d.voice_label,
          person_id: d.person_id,
          color: getSpeakerColor(speakers, d),
        };
      });
    });

    return mappedData.flat().filter((item): item is Exclude<typeof item, null> => item !== null);
  }, [isFetched, data, speakers, diarizationEnabled]);

  return {
    data: formatedData,
    refetch,
    speakers,
    rawData: data,
  };
}
